import { Link } from '@tanstack/react-router'
import type { PitchSessionStatus } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Banner } from '@workspace/ui/components/banner'
import { Button } from '@workspace/ui/components/button'
import { EmptyState } from '@workspace/ui/components/empty-state'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { PitchCard } from '@workspace/ui/components/pitch-card'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { Eye, EyeOff, Landmark, Mic } from 'lucide-react'
import { useState } from 'react'
import { extractEvaluationData } from '@/features/pitch/utils/scores'
import { DocumentPreviewModal, type PreviewDocument } from '@/features/preview'
import {
  useGetPitchVideoUrlMutation,
  usePitchesQuery,
  useStartupQuery,
  useUpdatePitchMutation,
} from '@/hooks/api'
import { usePitchCoverImage } from '@/hooks/use-pitch-cover-image'
import { PitchTimeline } from './pitch-timeline'
import { VideoPreviewModal } from './video-preview-modal'

const founderStatusDescriptions: Partial<Record<PitchSessionStatus, string>> = {
  PENDING: 'Your pitch session is being set up',
  ACTIVE: 'Your session is ready — join to start pitching',
  IN_PROGRESS: 'Pitch session is live',
  FAILED: 'Something went wrong with this session',
  INCOMPLETE: 'This session ended before completion',
}

export function FounderPitchHistory() {
  const { data: startupResponse } = useStartupQuery()
  const startupId = startupResponse?.data?.id
  const {
    data: pitchSessionsResponse,
    isLoading,
    error,
  } = usePitchesQuery({ startupId: startupId ?? '' }, { enabled: !!startupId })
  const pitchSessions = pitchSessionsResponse?.data ?? []

  const latestCompleted = pitchSessions.find((s) => s.status === 'COMPLETED')
  const restSessions = latestCompleted
    ? pitchSessions.filter((s) => s.id !== latestCompleted.id)
    : pitchSessions

  // Compute scores for the latest completed pitch
  const {
    totalScore: latestTotalScore,
    metricScores: latestMetricScores,
    winnerJudge: latestWinnerJudge,
  } = extractEvaluationData(latestCompleted?.evaluations?.[0])
  const coverImage = usePitchCoverImage(latestCompleted?.id ?? '')
  const updatePitch = useUpdatePitchMutation(latestCompleted?.id ?? '')
  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const getVideoUrlMutation = useGetPitchVideoUrlMutation({
    onSuccess: (data) => {
      setVideoUrl(data.data.url)
    },
  })

  const handleVideoClick = () => {
    if (!latestCompleted?.videoKey) return
    getVideoUrlMutation.mutate({ path: { pitchId: latestCompleted.id } })
  }

  const handleTogglePublish = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!latestCompleted) return
    updatePitch.mutate({
      path: { pitchId: latestCompleted.id },
      body: { isPublished: !latestCompleted.isPublished },
    })
  }

  return (
    <div className="space-y-8 py-8">
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="md" />
        </div>
      ) : error ? (
        <ErrorBanner message={error.message || 'Failed to load pitch sessions'} />
      ) : pitchSessions.length === 0 ? (
        <EmptyState
          icon={<Mic className="w-12 h-12" />}
          title="No pitches yet"
          description="Start your first pitch session to see your history here"
        />
      ) : (
        <>
          {latestCompleted && (
            <ProfileSection title="Latest Pitch">
              <PitchCard
                pitchId={latestCompleted.id}
                totalScore={latestTotalScore}
                videoCoverImageUrl={latestCompleted.videoCoverImageUrl}
                finishedAt={latestCompleted.finishedAt ?? latestCompleted.createdAt}
                judgeScores={latestMetricScores}
                winnerJudge={latestWinnerJudge}
                pitchDeck={
                  latestCompleted.pitchDeckId
                    ? {
                        id: latestCompleted.pitchDeckId,
                        fileName: 'Pitch Deck',
                        label: 'Pitch Deck',
                      }
                    : null
                }
                onVideoClick={latestCompleted.videoKey ? handleVideoClick : undefined}
                onPitchDeckClick={(deck) =>
                  setPreviewDocument({
                    id: deck.id,
                    fileName: deck.fileName,
                    mimeType: 'application/pdf',
                  })
                }
                onCoverImageUpload={coverImage.triggerUpload}
                onCoverImageRemove={coverImage.removeCoverImage}
                renderViewDetailsButton={(pitchId) => (
                  <Button variant="outline-primary" size="sm" className="w-full" asChild>
                    <Link to="/pitches/$pitchId" params={{ pitchId }}>
                      View Details
                    </Link>
                  </Button>
                )}
              />
              <input
                ref={coverImage.fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={coverImage.handleFileChange}
              />
              <Banner
                variant={latestCompleted.isPublished ? 'success' : 'warning'}
                icon={
                  latestCompleted.isPublished ? (
                    <Eye className="size-5" />
                  ) : (
                    <EyeOff className="size-5" />
                  )
                }
                title={latestCompleted.isPublished ? 'Published' : 'Not published'}
                description={
                  latestCompleted.isPublished ? (
                    <>
                      Visible in your{' '}
                      <Link
                        to="/share"
                        className="underline hover:text-emerald-400 transition-colors"
                      >
                        shared data room
                      </Link>
                    </>
                  ) : (
                    'Not visible in your shared data room'
                  )
                }
                actions={
                  <Button
                    variant={latestCompleted.isPublished ? 'outline' : 'default'}
                    size="sm"
                    onClick={handleTogglePublish}
                    disabled={updatePitch.isPending}
                  >
                    {latestCompleted.isPublished
                      ? updatePitch.isPending
                        ? 'Updating…'
                        : 'Unpublish'
                      : updatePitch.isPending
                        ? 'Publishing…'
                        : 'Publish'}
                  </Button>
                }
                className="mt-3"
              />
            </ProfileSection>
          )}

          {restSessions.length > 0 && (
            <ProfileSection title="History">
              <PitchTimeline
                pitches={restSessions}
                statusDescriptions={founderStatusDescriptions}
                renderBadges={(session) =>
                  (session.investorName || session.status === 'COMPLETED') && (
                    <div className="flex items-center gap-2 mt-1.5">
                      {session.investorName && (
                        <Badge variant="secondary" size="sm" className="gap-1">
                          <Landmark className="size-3" />
                          {session.investorName}
                        </Badge>
                      )}
                      {session.status === 'COMPLETED' &&
                        (session.isPublished ? (
                          <Badge variant="success" size="sm" className="gap-1">
                            <Eye className="size-3" />
                            Published
                          </Badge>
                        ) : (
                          <Badge variant="secondary" size="sm" className="gap-1">
                            <EyeOff className="size-3" />
                            Draft
                          </Badge>
                        ))}
                    </div>
                  )
                }
              />
            </ProfileSection>
          )}
        </>
      )}
      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open
          onOpenChange={(open) => !open && setPreviewDocument(null)}
        />
      )}
      {videoUrl && (
        <VideoPreviewModal
          videoUrl={videoUrl}
          title="Pitch Recording"
          open
          onOpenChange={(open) => !open && setVideoUrl(null)}
        />
      )}
    </div>
  )
}
